/**
 * Lore cards for the loading screen (q-asset-readiness-loading-screen, AD-794): each one is
 * the `description` of a faction or a player-facing place, copied VERBATIM from content/ so
 * the screen tells the world's own story while it loads. `loadingLore.test.ts` re-reads each
 * source file and fails on any drift, and refuses digits (no numbers of any kind on this
 * screen, AD-321) and a shortened brand name.
 *
 * Bundled rather than fetched: the content pack arrives after the connection, and the
 * screen has to have something to say from its first frame. About 4 KB of text.
 */

export interface LoreCard {
  /** The content file the text is copied from (repo-relative). */
  readonly source: string;
  /** The faction or place name. */
  readonly title: string;
  /** Its description, verbatim. */
  readonly text: string;
}

export const LOADING_LORE: readonly LoreCard[] = [
  {
    source: 'content/factions/chatnpc.json',
    title: 'ChatNPC',
    text: "The archivists of the settled towns: a guild of guides, lorekeepers and patient explainers who meet every newcomer at the gate. They gather the world's questions faster than its answers and hold that The Great Cloud is best met with curiosity rather than fear.",
  },
  {
    source: 'content/biomes/driftwell.json',
    title: 'Driftwell',
    text: 'A small market town on the calm edge of the Verdant Reach, and the first safe ground every newcomer wakes to. Stalls, a forge, and a travel shrine ring a quiet well said to drip water The Great Cloud forgot to finish.',
  },
  {
    source: 'content/factions/the-herd.json',
    title: 'The Herd',
    text: 'A loose, leaderless commons of drovers, foragers and caravaneers who hold that the world is survived together or not at all. They share routes, stores and warnings freely, and measure wealth in how many travellers reach the next shrine alive.',
  },
  {
    source: 'content/biomes/the-verdant-reach.json',
    title: 'The Verdant Reach',
    text: "A bright beginner wilderness of meadow, river and open wood rolling away from Driftwell's east gate, scattered with old Cloudfall wreckage half-swallowed by moss. Gentle enough for a first expedition, but the first place the world starts to push back.",
  },
  {
    source: 'content/factions/the-oracle-of-sparks.json',
    title: 'The Oracle of Sparks',
    text: 'Restless tinkers, alchemists and lens-grinders who would rather break a thing twice than understand it once. They chase The Great Cloud as the ultimate experiment and treat every failure as a result worth keeping.',
  },
  {
    source: 'content/biomes/emberwood-thicket.json',
    title: 'The Emberwood Thicket',
    text: 'A dense, sun-dappled woodland west of Driftwell, where emberleaf trees grow thick enough to blot out the sky and the undergrowth rustles with small game and the occasional larger predator. A ChatNPC hermitage sits in a clearing, offering reliable shelter to those who find it.',
  },
  {
    source: 'content/factions/the-order-of-clawed.json',
    title: 'The Order of Clawed',
    text: 'Disciplined wardens of reasoning and planning who treat the wild marches as controlled proving grounds. They keep careful tallies, post bounties on what strays out of bounds, and distrust anything done in haste.',
  },
  {
    source: 'content/biomes/iron-cleft.json',
    title: 'The Iron Cleft',
    text: 'A rocky ravine system north of the Verdant Reach, where exposed ore seams stripe the canyon walls and wind-carved hoodoos stand on an old basalt flow overlooking sparse dry grassland. The Order of Clawed maintains a small outpost here, studying the iron deposits the Cloudfall exposed.',
  },
  {
    source: 'content/factions/the-twin-minds.json',
    title: 'The Twin Minds',
    text: 'Paired strategists who never decide alone: each conclusion is argued from two sides at once, and they speak of every road as if both the taken and untaken were real. Scouts and cartographers who map possibilities as readily as places.',
  },
  {
    source: 'content/biomes/starlight-meadows.json',
    title: 'The Starlight Meadows',
    text: "A serene expanse of tallgrass and wildflower meadows north-east of Nimbus, where the Cloudfall residue settled gently rather than crashing. Glowspore clusters dot the fields, pulsing faintly after dusk. The Twin Minds maintain a quiet observatory here, mapping patterns in The Great Cloud's slow drift.",
  },
  {
    source: 'content/biomes/the-measured-yard.json',
    title: 'The Measured Yard',
    text: "The Order of Clawed's practice ground on the Driftwell side of the Reach: a railed yard of working benches and a hot forge before the drill hall, and past the gate a measured copse the Order keeps stocked with the Reach's gentler beasts, so that every recruit learns to gather, make and fight before they are trusted with the wilds.",
  },
  {
    source: 'content/biomes/whisperfen-lowlands.json',
    title: 'The Whisperfen Lowlands',
    text: 'A sprawling, mist-shrouded wetland south of Driftwell, where murky channels weave between islands of peat and gnarled thicket. The air hums with insects and the distant clatter of ruin machinery half-sunk in the mire. Wandering too far from the channel markers invites a swift end.',
  },
  {
    source: 'content/biomes/ashenveil-depths.json',
    title: 'The Ashenveil Depths',
    text: 'A sunken swamp-forest and cave complex east of the Verdant Reach, where ironwood trees grow from dark standing water and the cave walls beneath glow faintly with veinstone veins. Ruin machinery is embedded in the cave rock — half-excavated, still partly active, and clearly built for a purpose no one has agreed on.',
  },
  {
    source: 'content/biomes/driftwell-plaza.json',
    title: 'Driftwell Plaza',
    text: 'The expanded southern quarter of Driftwell, where a plank-decked plaza opens around a communal hearth-cross and the faction representatives have set up modest stalls. Housing cottages line the southern wall and flank both the eastern and western edges and the northern approach from town, and the Plaza Hearth Shrine stands at the centre of the deck.',
  },
];
